// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isEqual from 'lodash.isequal';

import styles from './styles.scss';

import { setHoveredStudioInternalName } from '../../redux/actionsCreators';
import { getCategory, getItems } from '../../utils/methods.js';
import {
  tweenOutRight,
  tweenOutLeft,
  tweenInRight,
  tweenInLeft,
  preloader,
  fadeInRight,
  animateMatrix,
  studiosNav,
  fadeOutLeft,
  fadeOutRight,
} from '../../utils/animation';
import { Studio } from '../../components';
import Specifications from './Specifications';

import type { ItemType } from '../../api/types';
import type { DefaultStateType } from '../../redux/reducers';

type Props = {
  match: Object,
  history: Object,
  studios: Array<ItemType>,
  setHoveredStudioInternalName: (?string) => void,
};

type State = {
  activeStudio: ItemType | null,
  transition: boolean,
};

class Studios extends React.Component<Props, State> {
  state = {
    activeStudio: null,
    transition: true,
  };

  componentDidMount() {
    if (document && document.body) {
      document.body.classList.add('studios');
    }

    this.checkURLParams();

    this.runPreloader();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const activeStudio = nextProps.studios.find(studio => studio.internalName === nextProps.match.params.internalName);

    if (!prevState.activeStudio || !isEqual(prevState.activeStudio, activeStudio)) {
      return { activeStudio, transition: true };
    }

    return null;
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (!this.props.match.params.internalName) {
      this.checkURLParams();
    }
  };

  shouldComponentUpdate = (nextProps, nextState) =>
    !isEqual(this.props.studios, nextProps.studios) || !isEqual(this.state, nextState);

  componentWillUnmount() {
    // $FlowFixMe
    document.body.classList.remove('studios');
    // $FlowFixMe
    document.body.classList.remove('roomPhotos');
    // $FlowFixMe
    document.body.classList.remove('panorama');
    // $FlowFixMe
    document.body.classList.remove('campusMap');
    //$FlowFixMe
    document.body.classList.remove('loaded');
  }

  runPreloader = () => {
    const callback = () => {
      studiosNav().play();

      //$FlowFixMe
      document.body.classList.add('loaded');

      animateMatrix();

      fadeInRight('[data-animation="fadeInRightTop"]');
      fadeInRight('[data-animation="fadeInRightBottom"]').delay(0.2);
    };

    return preloader(callback).play();
  };

  checkURLParams = () => {
    const { match, history, studios } = this.props;

    const studio = studios.find(studio => studio.internalName === match.params.internalName);

    if (!studio && studios.length > 0) {
      return history.push(`/studios/${studios[0].internalName}`);
    }
  };

  handleClick = (index: number) => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;

    const activeTab = Number(localStorage.getItem('activeTab'));

    if (activeTab === 0) {
      if (Number(this.state.activeStudio.name) > index) {
        // $FlowFixMe
        const callback = () => {
          tweenInLeft();
          fadeInRight('[data-animation="fadeInRightTop"]').delay(0.4);
          fadeInRight('[data-animation="fadeInRightBottom"]').delay(0.8);
        };

        tweenOutRight(callback);
        fadeOutRight('[data-animation="fadeInRightTop"]').delay(0.2);
        fadeOutRight('[data-animation="fadeInRightBottom"]').delay(0.4);
      } else {
        // $FlowFixMe
        const callback = () => {
          tweenInRight();
          fadeInRight('[data-animation="fadeInRightTop"]').delay(0.4);
          fadeInRight('[data-animation="fadeInRightBottom"]').delay(0.8);
        };

        tweenOutLeft(callback);
        fadeOutLeft('[data-animation="fadeInRightTop"]').delay(0.2);
        fadeOutLeft('[data-animation="fadeInRightBottom"]').delay(0.4);
      }
    }
  };

  navigateToStudio = (internalName: string, name: string) => {
    if (this.state.activeStudio.name === name) {
      return;
    }

    // set timeout before navigating to new route to provide Transition with time for animation
    this.handleClick(name);
    setTimeout(() => this.props.history.push(`/studios/${internalName}`), 500);
  };

  get specifications() {
    return this.state.activeStudio ? this.state.activeStudio.attributes.filter(attr => attr.type === 'text') : [];
  }

  render(): React.Node {
    const { studios, match } = this.props;
    const { activeStudio, transition } = this.state;

    return activeStudio ? (
      <React.Fragment>
        <div className={styles['page-wrapper']}>
          <div className={styles['studio-wrapper']}>
            <div className={styles.flexWrap}>
              <Studio
                studio={activeStudio}
                index={activeStudio.id}
                transition={transition}
                navigateToStudio={this.navigateToStudio}
              />

              <ul id="studiosNav" className={styles.nav}>
                {studios.map(
                  (studio: ItemType, index: number): React.Element<'li'> => (
                    <li
                      key={studio.name}
                      className={classnames(styles.navItem, {
                        active: studio.internalName === match.params.internalName,
                      })}
                      onClick={(): void => this.navigateToStudio(studio.internalName, studio.name)}
                      onMouseEnter={() => setHoveredStudioInternalName(studio.internalName)}
                      onMouseLeave={() => setHoveredStudioInternalName('')}
                    >
                      <span>{studio.name}</span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>

          {this.specifications.length > 0 && (
            <Specifications items={this.specifications} studioInternalName={match.params.internalName} />
          )}
        </div>
      </React.Fragment>
    ) : null;
  }
}

const mapStateToProps = (state: DefaultStateType): { studios: Array<ItemType> | null } => ({
  studios: getItems(getCategory(state.categories, 'studios')),
});

const mapDispatchToProps = dispatch => ({
  setHoveredStudioInternalName(internalName) {
    dispatch(setHoveredStudioInternalName(internalName));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Studios);



// WEBPACK FOOTER //
// ./src/pages/Studios/index.js